import { useServer } from '@ottd/api';
import type { ServerRankingSort } from '@ottd/api';
import { useTranslation } from 'react-i18next';
import { Link, useParams, useSearchParams } from 'react-router-dom';

import { BevelWindow } from '@/components/prototype/bevel-window';
import { ConnectDialog } from '@/components/prototype/connect-dialog';
import { LiveBadge } from '@/components/prototype/live-badge';
import { QueryState } from '@/components/prototype/query-state';
import { TypeTile } from '@/components/prototype/type-tile';
import { readPage, withPage } from '@/lib/page-param';
import { serverTypeTheme } from '@/lib/server-type';
import { serverFacts } from '@/pages/servers/server-facts';
import { ServerRanking } from '@/pages/servers/server-ranking';
import { bannerStyle } from '@/pages/shared';

/** `?sort=time` picks the time ranking; anything else is the default score ranking. */
const SORT_PARAM = 'sort';

function readSort(params: URLSearchParams): ServerRankingSort {
  return params.get(SORT_PARAM) === 'time' ? 'time' : 'points';
}

/**
 * Single server screen (legacy `/server/{id}`): identity banner with the
 * connection facts beside it, then the server's all-time ranking laid out like
 * the season ladder (podium + standings), 100 rows a page (`?page=N`), by
 * score or by time (`?sort=time`).
 */
export default function ServerPage() {
  const { t } = useTranslation('server');
  const { id: rawId } = useParams<{ id: string }>();
  const id = rawId !== undefined && /^\d+$/.test(rawId) ? Number(rawId) : undefined;
  const [params, setParams] = useSearchParams();
  const page = readPage(params);
  const sort = readSort(params);
  const { data: server, isLoading, isError, error } = useServer(id, page, sort);
  const theme = serverTypeTheme(server?.type ?? null);

  const facts = server
    ? [
        ...serverFacts(server),
        { key: t('facts.playersOnline'), value: String(server.playersNow), tone: 'text-text' },
      ]
    : [];

  // Pushes a history entry, so the back button walks back through the pages.
  const setPage = (next: number) => {
    setParams(withPage(params, next));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Another ranking starts again from page 1; score is the plain URL.
  const setSort = (next: ServerRankingSort) => {
    const nextParams = withPage(params, 1);
    if (next === 'time') nextParams.set(SORT_PARAM, next);
    else nextParams.delete(SORT_PARAM);
    setParams(nextParams);
  };

  return (
    <div className="mx-auto max-w-[1080px] px-4 py-6 sm:px-5">
      <QueryState
        isLoading={isLoading}
        isError={isError}
        error={error}
        isEmpty={id === undefined}
        emptyLabel={t('detail.unknown')}
      >
        {server ? (
          <div className="flex flex-col gap-8">
            <div className="grid gap-4 md:grid-cols-4">
              <div
                className="bevel shadow-hard-sm relative flex items-end gap-4 p-4 md:col-span-3"
                style={bannerStyle(server.type)}
              >
                <TypeTile type={server.type} size={56} climate={server.climate} />
                <div className="min-w-0">
                  <h1
                    className="font-display truncate text-xl text-white"
                    style={{ textShadow: '2px 2px 0 #000' }}
                  >
                    {server.name}
                  </h1>
                  <p className="font-mono text-xs" style={{ color: theme.subColor }}>
                    {theme.label.toUpperCase()}
                  </p>
                </div>
                {server.isLive ? <LiveBadge className="absolute right-3 top-3" /> : null}
              </div>

              <BevelWindow title={t('detail.panelTitle')} className="md:col-span-1">
                <div className="flex flex-col gap-1">
                  {facts.map((fact) => (
                    <div key={fact.key} className="flex flex-col py-0.5">
                      <span className="text-faint text-[10px] uppercase tracking-wide">
                        {fact.key}
                      </span>
                      <span className={`font-mono text-[13px] break-all ${fact.tone}`}>
                        {fact.value}
                      </span>
                    </div>
                  ))}
                  <div className="flex flex-col py-0.5">
                    <span className="text-faint text-[10px] uppercase tracking-wide">
                      {t('facts.currentGame')}
                    </span>
                    <span className="font-mono text-[13px]">
                      {server.currentGameId ? (
                        <Link to={`/game/${server.currentGameId}`} className="text-green">
                          #{server.currentGameId}
                        </Link>
                      ) : (
                        <span className="text-dim">{t('facts.idle')}</span>
                      )}
                    </span>
                  </div>
                  {/* Joinable only while a game runs (legacy: no game, no button). */}
                  {server.currentGameId !== null ? (
                    <ConnectDialog
                      address={server.address}
                      inviteCode={server.inviteCode}
                      serverName={server.name}
                      triggerClassName="mt-2 w-full"
                    />
                  ) : null}
                </div>
              </BevelWindow>
            </div>

            <ServerRanking
              server={server}
              page={page}
              sort={sort}
              onPageChange={setPage}
              onSortChange={setSort}
            />
          </div>
        ) : null}
      </QueryState>
    </div>
  );
}
